import { Icon, Panes } from '@wtfalch/design';
import type { MailboxNode } from '@wtfalch/email/mailbox';
import { SNOOZED_MAILBOX, walk } from '@wtfalch/email/mailbox';
import { useEffect, useRef, useState } from 'react';

import { folderName } from './format.ts';
import { MailboxIcon } from './icons.tsx';

/**
 * The folder column -- `Mail.dc.html`'s third pane, mirrored to the left of
 * the list the way Chat and Calls already draw their third column on the right
 * (agora#287). System folders (a role: Inbox, Drafts, Sent, ...) first, then
 * this account's own, each selectable; a `+` makes another with
 * `createMailbox`. `mail.css` drops this column below the canvas's lower
 * breakpoint, where `MailList`'s own mailbox picker takes the job back over.
 *
 * Browsing by label needs a filtered view this column's `mailboxId`
 * selection has no place for yet; labels show on the rows and in the thread
 * toolbar instead.
 */
/** The canvas's order, whatever order the server lists its own in. */
const ROLE_ORDER = ['inbox', 'snoozed', 'sent', 'drafts', 'archive', 'junk', 'trash'];

/** The mailbox `snoozeThread` files snoozed threads into: top level, named
 *  `SNOOZED_MAILBOX`, with no role (JMAP has none for it). It sits with the
 *  system folders, not under "Folders", since the person never made it. */
const isSnoozed = (m: MailboxNode) =>
  m.role === null && m.path.length === 1 && m.name === SNOOZED_MAILBOX;

const roleRank = (m: MailboxNode) => {
  const at = ROLE_ORDER.indexOf(m.role ?? (isSnoozed(m) ? 'snoozed' : ''));
  return at === -1 ? ROLE_ORDER.length : at;
};

/** Folders another account has shared with this login. */
export type SharedGroup = { accountId: string; name: string; folders: readonly MailboxNode[] };

export function MailFolders({
  tree,
  mailboxId,
  onSelectMailbox,
  onCreateFolder,
  onSettings,
  shared = [],
  sharedAccount = null,
  onSelectShared,
}: {
  tree: readonly MailboxNode[] | null;
  mailboxId: string | null;
  onSelectMailbox: (mailboxId: string) => void;
  /** Rejects on failure; `MailFolders` leaves the new-folder field open so
   *  the name is not lost, the same way a failed inline edit anywhere else
   *  in this app keeps its own input up. */
  onCreateFolder: (name: string) => Promise<void>;
  onSettings?: () => void;
  /** Folders colleagues have shared with this login, one group per account. */
  shared?: readonly SharedGroup[];
  /** The account whose folder is open, `null` for the login's own. */
  sharedAccount?: string | null;
  onSelectShared?: (accountId: string, mailboxId: string) => void;
}) {
  const flat = tree ? [...walk(tree)] : [];
  const system = flat
    .filter((m) => m.role !== null || isSnoozed(m))
    .sort((a, b) => roleRank(a) - roleRank(b));
  const custom = flat.filter((m) => m.role === null && !isSnoozed(m));

  return (
    <Panes.Column width={176} surface="panel" className="mail-folders">
      <nav className="flex flex-col" aria-label="Folders">
        <div className="flex flex-col gap-0.25 px-2">
          {system.map((m) => (
            <FolderRow
              key={m.id}
              mailbox={m}
              selected={sharedAccount === null && m.id === mailboxId}
              onClick={() => onSelectMailbox(m.id)}
            />
          ))}
        </div>
        <div className="flex flex-col mt-3">
          <div className="flex items-center gap-2 mail-folder-group-head">
            <span className="mail-folder-group-label flex-1">Folders</span>
            <NewFolderButton onCreate={onCreateFolder} />
          </div>
          <div className="flex flex-col gap-0.25 px-2">
            {custom.map((m) => (
              <FolderRow
                key={m.id}
                mailbox={m}
                selected={sharedAccount === null && m.id === mailboxId}
                onClick={() => onSelectMailbox(m.id)}
              />
            ))}
          </div>
        </div>
        {shared.map((group) => (
          <div key={group.accountId} className="flex flex-col mt-3">
            <div className="mail-folder-group-head">
              <span className="mail-folder-group-label flex-1">{`Shared by ${group.name}`}</span>
            </div>
            <div className="flex flex-col gap-0.25 px-2">
              {group.folders.map((m) => (
                <FolderRow
                  key={m.id}
                  mailbox={m}
                  selected={sharedAccount === group.accountId && m.id === mailboxId}
                  onClick={() => onSelectShared?.(group.accountId, m.id)}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>
      {onSettings && (
        <div className="px-2 mt-auto">
          <button type="button" className="mail-folder-row" onClick={onSettings}>
            <Icon name="settings" size={16} />
            <span className="mail-folder-label flex-1 min-w-0 truncate">Mail settings</span>
          </button>
        </div>
      )}
    </Panes.Column>
  );
}

function FolderRow({
  mailbox,
  selected,
  onClick,
}: {
  mailbox: MailboxNode;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={selected ? 'mail-folder-row on' : 'mail-folder-row'}
      style={
        mailbox.role || isSnoozed(mailbox)
          ? undefined
          : { paddingLeft: `${(mailbox.path.length - 1) * 14 + 10}px` }
      }
      onClick={onClick}
      title={folderName(mailbox)}
      aria-current={selected ? 'true' : undefined}
    >
      {mailbox.role ? (
        <MailboxIcon role={mailbox.role} size={16} />
      ) : isSnoozed(mailbox) ? (
        <Icon name="clock" size={16} />
      ) : (
        <Icon name="folder" size={16} />
      )}
      <span className="mail-folder-label flex-1 min-w-0 truncate">{folderName(mailbox)}</span>
      {mailbox.unreadThreads > 0 && (
        <span className="mail-folder-unread flex-none text-muted text-xs">
          {mailbox.unreadThreads}
        </span>
      )}
    </button>
  );
}

/** The `+` and the inline field it opens. Its own component so the text
 *  input's state does not force the whole column to re-render on every
 *  keystroke. Enter or blurring with a name submits; Escape or blurring
 *  empty closes without creating anything. */
function NewFolderButton({ onCreate }: { onCreate: (name: string) => Promise<void> }) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Focused from an effect rather than the `autoFocus` attribute: the field
  // only exists once `creating` is true, so there is no mount to hijack the
  // way `autoFocus` normally would -- this runs once, the moment the field
  // itself appears.
  useEffect(() => {
    if (creating) inputRef.current?.focus();
  }, [creating]);

  if (!creating) {
    return (
      <button
        type="button"
        className="mail-folder-add"
        aria-label="New folder"
        onClick={() => setCreating(true)}
      >
        <Icon name="plus" size={16} />
      </button>
    );
  }

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setCreating(false);
      return;
    }
    setBusy(true);
    setError('');
    try {
      await onCreate(trimmed);
      setName('');
      setCreating(false);
    } catch (err) {
      // Left open, with the name still in it, so a rejected create is not
      // also a lost one -- `onCreate`'s own contract, above -- and the
      // reason shown beside it.
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="mail-folder-new"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <input
        ref={inputRef}
        className="mail-folder-new-input"
        value={name}
        disabled={busy}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => void submit()}
        onKeyDown={(e) => {
          if (e.key !== 'Escape') return;
          setCreating(false);
          setName('');
        }}
        placeholder="Folder name"
        aria-label="New folder name"
      />
      <p className="text-xs text-muted mail-folder-new-hint">Enter to add, Esc to cancel</p>
      {error && (
        <p className="text-xs text-bad m-0 pt-1" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
